/**
 * ProcessUndoButton — surfaces the shared mutation journal's `undo()` in the
 * process-view chrome (plan §4c feedback). Mounts next to the EditToggle and
 * shows only while editing; disabled when the journal is empty. The journal is
 * undo-only (each undo re-applies the inverse as a NEW edit — see
 * `mutations.ts`), so there is no redo affordance here; keyboard ⌘Z routing is
 * deferred to the keyboard-accelerator phase (it must arbitrate with the
 * board's own ⌘Z).
 */
import { useContext } from "react";
import { Undo2 } from "lucide-react";
import { IconButton } from "@archlang/ui";
import { EditModeContext } from "../EditModeContext.js";
import type { ModelMutationsApi } from "../mutations.js";

export interface ProcessUndoButtonProps {
  readonly mutations: ModelMutationsApi | null;
  readonly className?: string;
}

export function ProcessUndoButton({ mutations, className }: ProcessUndoButtonProps) {
  // Optional read (mirrors EditToggle): a chrome primitive may mount outside a
  // provider (isolated tests) — degrade to nothing rather than throw.
  const api = useContext(EditModeContext);
  if (!api?.editMode || !mutations) return null;
  // Read live each render; a mutation / undo recompiles the model → re-render →
  // fresh depth. No local state to drift.
  const depth = mutations.undoDepth();
  return (
    <IconButton
      size="sm"
      variant="ghost"
      disabled={depth === 0}
      aria-label="Undo last edit"
      title={depth === 0 ? "Nothing to undo" : "Undo last edit"}
      data-process-undo=""
      className={className}
      onClick={() => mutations.undo()}
    >
      <Undo2 size={14} />
    </IconButton>
  );
}
